import React, { useEffect, useState } from "react";
import axios from "axios";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FiPlus, FiTrash2, FiSave, FiUpload, FiLink } from 'react-icons/fi';
import { FaSpinner } from 'react-icons/fa';
import { toast } from "react-toastify";

const API_URL = `${process.env.REACT_APP_API_URL}/api/about`;

// Quill editor configuration
const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'header': [1, 2, 3, false] }],
    [{ 'align': [] }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'indent': '-1' }, { 'indent': '+1' }],
    ['link'],
    ['clean']
  ],
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent',
  'link', 'align'
];

// Helper function to safely parse core values
const parseCoreValues = (coreValues) => {
  try {
    if (Array.isArray(coreValues)) return coreValues;
    if (typeof coreValues === 'string') {
      let parsed = coreValues;
      try {
        parsed = JSON.parse(coreValues);
        if (typeof parsed === 'string') parsed = JSON.parse(parsed);
      } catch (e) {
        console.log("Couldn't parse core_values as JSON");
      }
      
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === 'string') {
        return parsed.split(/[\n,]/).filter(val => val.trim() !== "");
      }
    }
    return [""];
  } catch (e) {
    console.error("Error parsing core values:", e);
    return [""];
  }
};

const UpdateAbout = () => {
  const [formData, setFormData] = useState({
    introduction: "",
    mission: "",
    vision: "",
    purpose: "",
    videoFile: null,
    videoLink: "",
    core_values: [""],
  });

  const [isEditing, setIsEditing] = useState(false);
  const [aboutId, setAboutId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [videoInputType, setVideoInputType] = useState('link'); // 'link' or 'file'
  const [videoPreview, setVideoPreview] = useState(null);
  const [initialData, setInitialData] = useState(null);

  // Fetch existing data
  useEffect(() => {
    const fetchAbout = async () => {
      try {
        setLoading(true);
        const res = await axios.get(API_URL);
        if (res.data?.data) {
  
      
          const { id, introduction, mission, vision, purpose, videoFile, core_values } = res.data.data;
          
          const parsedCoreValues = parseCoreValues(core_values);
                setInitialData(res.data.data); // Store initial data

          setFormData({
            introduction: introduction || "",
            mission: mission || "",
            vision: vision || "",
            purpose: purpose || "",
            videoFile: videoFile || null,
            videoLink: videoFile?.startsWith('http') ? videoFile : "",
            core_values: parsedCoreValues.length > 0 ? parsedCoreValues : [""],
          });

          // Set video preview if it's a link
          if (videoFile?.startsWith('http')) {
            setVideoPreview(videoFile);
            setVideoInputType('link');
          } else if (videoFile) {
            setVideoPreview(`${process.env.REACT_APP_API_URL}${videoFile}`);
            setVideoInputType('file');
          }
          
          setAboutId(id);
          setIsEditing(true);
        }
      } catch (err) {
        console.log("No about_us data yet or error fetching:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAbout();
  }, []);

  // Handle rich text changes
  const handleRichTextChange = (value, field) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle core value change
  const handleCoreValueChange = (index, value) => {
    const updated = [...formData.core_values];
    updated[index] = value;
    setFormData(prev => ({
      ...prev,
      core_values: updated,
    }));
  };

  // Add new core value
  const addCoreValue = () => {
    setFormData(prev => ({
      ...prev,
      core_values: [...prev.core_values, ""],
    }));
  };

  // Remove core value
  const removeCoreValue = (index) => {
    if (formData.core_values.length <= 1) {
      toast.warning("You must have at least one core value");
      return;
    }
    
    const updated = [...formData.core_values];
    updated.splice(index, 1);
    setFormData(prev => ({
      ...prev,
      core_values: updated,
    }));
  };

  // Handle video file change
  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-ms-wmv'];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a valid video file (MP4, MOV, AVI, WMV)");
      return;
    }

    // Validate file size (50MB max)
    if (file.size > 500 * 1024 * 1024) {
      toast.error("Video file size must be less than 50MB");
      return;
    }

    setFormData(prev => ({
      ...prev,
      videoFile: file,
      videoLink: ""
    }));

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setVideoPreview(previewUrl);
  };

  // Handle video link change
  const handleVideoLinkChange = (e) => {
    const link = e.target.value;
    setFormData(prev => ({
      ...prev,
      videoLink: link,
      videoFile: null
    }));

    // Simple YouTube URL validation
    if (link.includes('youtube.com') || link.includes('youtu.be')) {
      setVideoPreview(link);
    } else if (link === "") {
      setVideoPreview(null);
    }
  };
const handleRemoveVideo = () => {
  setFormData(prev => ({
    ...prev,
    videoFile: null,
    videoLink: ""
  }));
  setVideoPreview(null);
  
  // Clear the file input if it exists
  const fileInput = document.querySelector('input[type="file"]');
  if (fileInput) fileInput.value = "";
};
  // Submit form
// Submit form
const handleSubmit = async (e) => {
  e.preventDefault();
  setSubmitting(true);

  try {
    // Filter out empty values but ensure at least one remains
    const nonEmptyValues = formData.core_values.filter(val => val.trim() !== "");
    const finalCoreValues = nonEmptyValues.length > 0 ? nonEmptyValues : [""];
    
    // Prepare form data for file upload
    const formDataToSend = new FormData();
    formDataToSend.append('introduction', formData.introduction);
    formDataToSend.append('mission', formData.mission);
    formDataToSend.append('vision', formData.vision);
    formDataToSend.append('purpose', formData.purpose);
    formDataToSend.append('core_values', JSON.stringify(finalCoreValues));

    // NEW: Track if video was removed
    const videoWasRemoved = videoPreview === null && 
                           (formData.videoFile || formData.videoLink || 
                            (initialData && initialData.videoFile));
    
    if (videoWasRemoved) {
      formDataToSend.append('removeVideo', 'true');
    }
    // Add video data based on input type
    else if (videoInputType === 'file' && formData.videoFile) {
      formDataToSend.append('videoFile', formData.videoFile);
    } 
    else if (videoInputType === 'link' && formData.videoLink) {
      formDataToSend.append('videoLink', formData.videoLink);
    }

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    };

    if (isEditing) {
      await axios.put(`${API_URL}/${aboutId}`, formDataToSend, config);
    } else {
      await axios.post(API_URL, formDataToSend, config);
      setIsEditing(true);
    }
    toast.success("About information saved successfully");
    
    // NEW: Refetch data to update state
    const res = await axios.get(API_URL);
    if (res.data?.data) {
      const { videoFile } = res.data.data;
      setInitialData(res.data.data);
      
      // Update video preview state
      if (videoFile?.startsWith('http')) {
        setVideoPreview(videoFile);
      } else if (videoFile) {
        setVideoPreview(`${process.env.REACT_APP_API_URL}${videoFile}`);
      } else {
        setVideoPreview(null);
      }
    }
  } catch (err) {
    console.error("Failed to submit:", err);
    toast.error("Error saving About information");
  } finally {
    setSubmitting(false);
  }
};
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <FaSpinner className="animate-spin text-4xl text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6">
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {/* Form Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6 text-white">
          <h2 className="text-2xl font-bold">
            {isEditing ? "Edit About Page Content" : "Create About Page Content"}
          </h2>
          <p className="text-blue-100">
            Update the information that appears on your About page
          </p>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          {/* Introduction Section */}
          <div className="space-y-2">
            <label className="block text-lg font-medium text-gray-700">Introduction</label>
            <div className="border rounded-lg overflow-hidden">
              <ReactQuill
                theme="snow"
                value={formData.introduction}
                onChange={(value) => handleRichTextChange(value, 'introduction')}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Write your introduction here..."
                className="bg-white h-60"
              />
            </div>
          </div>

          {/* Mission Section */}
          <div className="space-y-2">
            <label className="block text-lg font-medium text-gray-700">Mission Statement</label>
            <div className="border rounded-lg overflow-hidden">
              <ReactQuill
                theme="snow"
                value={formData.mission}
                onChange={(value) => handleRichTextChange(value, 'mission')}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Describe your organization's mission..."
                className="bg-white h-60"
              />
            </div>
          </div>

          {/* Vision Section */}
          <div className="space-y-2">
            <label className="block text-lg font-medium text-gray-700">Vision Statement</label>
            <div className="border rounded-lg overflow-hidden">
              <ReactQuill
                theme="snow"
                value={formData.vision}
                onChange={(value) => handleRichTextChange(value, 'vision')}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Describe your organization's vision..."
                className="bg-white h-60"
              />
            </div>
          </div>

          {/* Purpose Section */}
          <div className="space-y-2">
            <label className="block text-lg font-medium text-gray-700">Purpose</label>
            <div className="border rounded-lg overflow-hidden">
              <ReactQuill
                theme="snow"
                value={formData.purpose}
                onChange={(value) => handleRichTextChange(value, 'purpose')}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Explain your organization's purpose..."
                className="bg-white h-52"
              />
            </div>
          </div>

          {/* Video Section */}
          <div className="space-y-4">
            <label className="block text-lg font-medium text-gray-700">Video</label>
            
            {/* Video Input Type Toggle */}
<div className="flex space-x-4 mb-4">
              <button
                type="button"
                onClick={() => setVideoInputType('link')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg ${videoInputType === 'link' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}
              >
                <FiLink />
                <span>YouTube Link</span>
              </button>
              <button
                type="button"
                onClick={() => setVideoInputType('file')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg ${videoInputType === 'file' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}
              >
                <FiUpload />
                <span>Upload Video</span>
              </button>
            </div>

            {/* Video Input Based on Type */}
            {videoInputType === 'link' ? (
              <div className="space-y-2">
                <input
                  type="url"
                  value={formData.videoLink}
                  onChange={handleVideoLinkChange}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-sm text-gray-500">Enter a YouTube URL</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <label className="flex-1 cursor-pointer">
                    <div className="p-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 transition-colors text-center">
                      <FiUpload className="mx-auto text-2xl text-gray-400 mb-2" />
                      <p className="text-gray-600">
                        {formData.videoFile ? formData.videoFile.name : "Click to upload video"}
                      </p>
                      <p className="text-sm text-gray-400 mt-1">
                        MP4, MOV, AVI or WMV (Max 50MB)
                      </p>
                    </div>
                    <input
                      type="file"
                      onChange={handleVideoFileChange}
                      accept="video/mp4,video/quicktime,video/x-msvideo,video/x-ms-wmv"
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            )}

            {/* Video Preview with Remove Button */}
            {videoPreview && (
              <div className="mt-4 relative">
                <h3 className="text-md font-medium text-gray-700 mb-2">Preview:</h3>
                {videoPreview.includes('youtube') || videoPreview.includes('youtu.be') ? (
                  <div className="relative pt-[56.25%] overflow-hidden rounded-lg bg-black">
                    <iframe
                      src={`https://www.youtube.com/embed/${getYouTubeId(videoPreview)}`}
                      className="absolute top-0 left-0 w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title="YouTube video preview"
                    />
                  </div>
                ) : (
                  <div className="rounded-lg overflow-hidden">
                    <video controls className="w-full max-h-96">
                      <source src={videoPreview} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleRemoveVideo}
                  className="absolute top-0 right-0 m-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  title="Remove video"
                >
                  <FiTrash2 size={16} />
                </button>
              </div>
            )}
          </div>

          {/* Core Values Section */}
          <div className="space-y-4">
            <label className="block text-lg font-medium text-gray-700">Core Values</label>
            <div className="space-y-3">
              {formData.core_values.map((value, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => handleCoreValueChange(index, e.target.value)}
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter a core value"
                      required
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeCoreValue(index)}
                    className={`mt-3 text-red-500 hover:text-red-700 transition-colors ${
                      formData.core_values.length <= 1 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    aria-label="Remove core value"
                    disabled={formData.core_values.length <= 1}
                  >
                    <FiTrash2 size={20} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addCoreValue}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors mt-2"
              >
                <FiPlus size={18} />
                <span>Add Another Core Value</span>
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={submitting}
              className="w-full md:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-70"
            >
              {submitting ? (
                <>
                  <FaSpinner className="animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <FiSave size={18} />
                  <span>{isEditing ? "Update About Page" : "Save About Page"}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Helper function to extract YouTube ID from URL
function getYouTubeId(url) {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
}

export default UpdateAbout;